import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import type { Product, ProductGroup } from "./types"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function shouldShowPrices(isLoggedIn: boolean, showPricesBeforeLoginOverride?: boolean | null): boolean {
  const showPricesBeforeLoginEnvEnabled = import.meta.env.VITE_SHOW_PRICES_BEFORE_LOGIN === '1'
  const showPricesBeforeLogin = showPricesBeforeLoginEnvEnabled && (
    typeof showPricesBeforeLoginOverride === 'boolean'
      ? showPricesBeforeLoginOverride
      : true
  )
  return showPricesBeforeLogin || isLoggedIn
}

export function requiresProductVariationSelection(
  product: Pick<Product, 'hasVariations' | 'id' | 'parentProductId' | 'variationAttributes' | 'packagingLabel'> | null | undefined
): boolean {
  if (product?.hasVariations !== true) return false

  const normalizedParentProductId = String(product.parentProductId ?? '').trim()
  if (normalizedParentProductId) {
    return false
  }

  const resolvedPackagingLabel = String(product.packagingLabel ?? '').trim()
  if (resolvedPackagingLabel) {
    return false
  }

  const hasResolvedPackagingSelection = Array.isArray(product.variationAttributes)
    && product.variationAttributes.some((attribute) => {
      const attributeKey = String(attribute.variationId ?? '').trim().toLowerCase()
      const attributeName = String(attribute.name ?? '').trim().toLowerCase()
      const attributeValue = String(attribute.value ?? '').trim()

      if (!attributeValue) return false

      return attributeKey === 'embalagem' || attributeName === 'embalagem'
    })

  if (hasResolvedPackagingSelection) {
    return false
  }

  return product.hasVariations === true
}

export function groupProducts(products: Product[]): (Product | ProductGroup)[] {
  const grouped = new Map<string, Product[]>()
  const ungrouped: Product[] = []

  products.forEach((product) => {
    if (product.groupId) {
      if (!grouped.has(product.groupId)) {
        grouped.set(product.groupId, [])
      }
      grouped.get(product.groupId)!.push(product)
    } else {
      ungrouped.push(product)
    }
  })

  const result: (Product | ProductGroup)[] = []

  grouped.forEach((variations, groupId) => {
    if (variations.length > 1) {
      const mainProduct = variations[0]
      result.push({
        groupId,
        mainProduct,
        variations,
        supplierName: mainProduct.supplierName,
        videoUrl: mainProduct.videoUrl,
      })
    } else {
      result.push(variations[0])
    }
  })

  result.push(...ungrouped)

  return result
}
